import type { JSX } from "react";
import { Chip, Tooltip, Box } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

/**
 * W4 — "Where in QuickBooks" badge. Given a transaction row and its joined QB
 * cache entry, render a chip + tooltip explaining exactly which QB Desktop
 * document type the row is posted as and where to find it.
 *
 * Critical UX driver: the client confused Credit Card Credits (refunds) with
 * Credit Card Charges, because the old UI didn't surface the QB txnType. This
 * badge makes the distinction loud and uses different colors/icons per type.
 */

export type QbCacheRow =
  | {
      qbTxnId: string;
      txnType: string;
      txnDate?: string;
      amount?: number;
      memo?: string;
      refNumber?: string;
      vendorName?: string;
      accountName?: string;
      lastSyncedAt?: number;
    }
  | null
  | undefined;

type Props = {
  qbTransactionId?: string | null;
  qb?: QbCacheRow;
  txDate: string;
  drift?: boolean;
};

type BadgeShape = {
  label: string;
  color: "success" | "info" | "default" | "warning" | "secondary";
  icon: JSX.Element;
};

function shapeFor(txnType: string | undefined): BadgeShape {
  switch (txnType) {
    case "CreditCardCharge":
      return { label: "Charge", color: "success", icon: <ArrowUpwardIcon fontSize="small" /> };
    case "CreditCardCredit":
      return { label: "Credit (refund)", color: "info", icon: <ArrowDownwardIcon fontSize="small" /> };
    case "Check":
      return { label: "Check", color: "default", icon: <ReceiptIcon fontSize="small" /> };
    case "Deposit":
      return { label: "Deposit", color: "success", icon: <ArrowDownwardIcon fontSize="small" /> };
    case "Bill":
      return { label: "Bill", color: "default", icon: <ReceiptIcon fontSize="small" /> };
    case "JournalEntry":
      return { label: "Journal", color: "secondary", icon: <CompareArrowsIcon fontSize="small" /> };
    default:
      return { label: txnType ?? "Posted", color: "default", icon: <HelpOutlineIcon fontSize="small" /> };
  }
}

export function QbPostedBadge({ qbTransactionId, qb, txDate, drift }: Props) {
  // Not yet posted to QB
  if (!qbTransactionId) {
    return (
      <Chip
        size="small"
        variant="outlined"
        label="Not posted"
        sx={{ fontSize: "0.72rem" }}
      />
    );
  }

  // Confirmed drift: QB verified this posted row is GONE (a human deleted it in QB).
  if (drift) {
    return (
      <Tooltip
        title={
          <Box>
            <div>
              <strong>Removed from QuickBooks</strong>
            </div>
            <div>This posted transaction no longer exists in QB.</div>
            <div>QB Ref: {qbTransactionId}</div>
          </Box>
        }
      >
        <Chip
          size="small"
          color="error"
          icon={<ReportProblemIcon fontSize="small" />}
          label="Drift"
          sx={{ fontSize: "0.72rem" }}
        />
      </Tooltip>
    );
  }
  // Posted, but we just have no local cache row yet — NOT a confirmed drift.
  if (!qb) {
    return (
      <Tooltip
        title={
          <Box>
            <div>
              <strong>Posted</strong> · not in local cache yet
            </div>
            <div>QB Ref: {qbTransactionId}</div>
          </Box>
        }
      >
        <Chip
          size="small"
          variant="outlined"
          icon={<HelpOutlineIcon fontSize="small" />}
          label="Posted"
          sx={{ fontSize: "0.72rem" }}
        />
      </Tooltip>
    );
  }

  const shape = shapeFor(qb.txnType);
  const dateShift =
    qb.txnDate && qb.txnDate !== txDate
      ? `(bank: ${txDate}, QB: ${qb.txnDate})`
      : null;

  return (
    <Tooltip
      title={
        <Box sx={{ whiteSpace: "pre-line" }}>
          <div>
            <strong>{qb.txnType}</strong>
          </div>
          {qb.accountName && <div>Account: {qb.accountName}</div>}
          {qb.refNumber && <div>Ref: {qb.refNumber}</div>}
          {qb.txnDate && <div>QB Date: {qb.txnDate}</div>}
          {dateShift && <div style={{ color: "#b00" }}>{dateShift}</div>}
          {qb.memo && (
            <div>
              Memo: {qb.memo.slice(0, 80)}
              {qb.memo.length > 80 ? "…" : ""}
            </div>
          )}
        </Box>
      }
    >
      <Chip
        size="small"
        color={shape.color}
        icon={shape.icon}
        label={shape.label}
        sx={{ fontSize: "0.72rem" }}
      />
    </Tooltip>
  );
}
